// Вкладка «История статусов»: кто, когда и на каком основании перевёл человека.
//
// Основание — не украшение записи, а ответ на вопрос «почему он в отпуске в разгар вахты»,
// который иначе задают в чате. Прямая просьба заказчика: «хорошо бы иметь примечание с
// указанием причины, чтобы не вспоминать». Поэтому отсутствие основания показано словами, а
// не пропущено: «основание не указано» — это тоже факт, и он говорит, что спросить надо у
// того, кто менял.

import { RiHistoryLine } from '@remixicon/react';

import { countLabel } from '@/shared/text/plural';
import type { EmployeeOut, StatusHistoryOut } from '@/shared/api/endpoints/people';
import { formatDateDMY, formatTimeHM, parseMoment } from '@/shared/time/format';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';

import { statusTitle } from '../dictionaries';
import { useStatusHistoryQuery } from '../queries';

export function StatusHistoryTab({ employee }: { employee: EmployeeOut }) {
  const query = useStatusHistoryQuery(employee.id);

  return (
    <div className='flex flex-col gap-4 p-5'>
      <ScreenState
        data={query.data}
        error={query.error}
        subject='история статусов'
        isEmpty={(data) => data.items.length === 0}
        onRetry={() => void query.refetch()}
        loading={
          <div className='flex flex-col gap-2'>
            <Skeleton className='h-14 w-full' />
            <Skeleton className='h-14 w-full' />
            <Skeleton className='h-14 w-full' />
          </div>
        }
        empty={
          <EmptyState
            icon={RiHistoryLine}
            title='Статус ни разу не менялся'
            description={`Человек с заведения так и стоит в состоянии «${statusTitle(
              employee.status,
            )}». Первая запись появится здесь, как только статус сменят.`}
          />
        }
      >
        {(data) => (
          <>
            <h4 className='px-1 pb-2 text-label-sm text-text-strong-950'>
              {countLabel(data.items.length, ['смена статуса', 'смены статуса', 'смен статуса'])}
            </h4>
            <ol className='flex flex-col rounded-xl bg-bg-white-0 px-4 ring-1 ring-inset ring-stroke-soft-200'>
              {data.items.map((entry) => (
                <li
                  key={entry.id}
                  className='flex flex-col gap-0.5 border-b border-stroke-soft-200 py-3 last:border-b-0'
                >
                  <span className='text-label-sm text-text-strong-950'>
                    {entry.status_from === null
                      ? `Человек заведён со статусом «${statusTitle(entry.status_to)}»`
                      : `«${statusTitle(entry.status_from)}» → «${statusTitle(entry.status_to)}»`}
                  </span>
                  <span className='text-paragraph-xs text-text-sub-600'>{whenWords(entry)}</span>
                  <span className='text-paragraph-xs text-text-sub-600'>
                    {entry.basis === null || entry.basis.trim() === ''
                      ? 'основание не указано — спросить у того, кто менял'
                      : `основание: ${entry.basis}`}
                  </span>
                </li>
              ))}
            </ol>
          </>
        )}
      </ScreenState>
    </div>
  );
}

/** Когда и кем. Идентификатор пользователя показываем только если имени взять неоткуда. */
function whenWords(entry: StatusHistoryOut): string {
  const moment = parseMoment(entry.changed_at);
  const when =
    moment === null ? 'время не записано' : `${formatDateDMY(moment)}, ${formatTimeHM(moment)}`;
  const who = entry.changed_by === null ? 'изменено системой' : 'изменил сотрудник системы';
  return `${when} · ${who}`;
}
